import { useRef, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { twJoin, twMerge } from 'tailwind-merge';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

import useStack from '@/data/hook/useStack';
import { ListAreaFilter, listAreaParents } from '@/data/services/areaServices';
import { areasQueryKeys, tagsQueryKeys } from '@/data/services/querykeys';
import Area from '@/models/Area';
import { bodyText, card } from '@/styles/styles';
import { extractParents, mountTopicTagName } from '@/utils/area';
import InfiniteSearchInput from '@/components/ui/custom/dataInput/InfiniteSearchInput';
import { REQUEST_STALE_TIME_IN_MS as staleTime } from '@/constants';
import useInfiniteService from '@/data/hook/useInfiniteService';
import useListService from '@/data/hook/useListService';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Checkbox from '@/components/ui/custom/dataInput/Checkbox';
import TopBar from '@/components/ui/custom/dataInput/TagInput/TopicsInput/TopBar';
import InfinityList from '@/components/ui/custom/InfinityList';
import { LoadingIcon } from '../icons';
import { TagWithName } from '@/models/Tag';

type TagsManagerProps = {
  currentTags: TagWithName[];
  onAdd: (tag: TagWithName) => void;
  disabled?: boolean;
  onDelete: (tagId: number) => void;
};

export default function TagsManager(props: TagsManagerProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'topics.input',
  });
  const { currentTags, onAdd, disabled, onDelete } = props;

  const stack = useStack<Area>();

  const currentArea = stack.getTop();

  async function onSelectArea(area: Area) {
    const parents = await listAreaParents(area.id);
    const parentsExtract = extractParents(parents.results[0]);
    if (area.isLeaf) {
      stack.setStack(parentsExtract.slice(0, -1));
      return;
    }
    stack.setStack(parentsExtract);
  }

  return (
    <section className={twMerge(card, 'flex flex-col max-h[16rem] w-[27rem]')}>
      <InfiniteSearchInput
        onSelect={onSelectArea}
        service={areasQueryKeys.list}
        displayName={(area: Area) => area.name}
        input={{
          disabled,
          placeholder: t('searchForTopics'),
          className: {
            base: 'w-full',
          },
        }}
        options={{
          staleTime,
        }}
      />
      <Tags
        currentTags={currentTags}
        area={currentArea}
        stack={stack}
        onAdd={onAdd}
        onDelete={onDelete}
      />
    </section>
  );
}

type TagsProps = {
  currentTags: TagWithName[];
  area?: Area;
  stack: ReturnType<typeof useStack<Area>>;
  onAdd: (tag: TagWithName) => void;
  onDelete: (tagId: number) => void;
};

function Tags(props: TagsProps) {
  const { currentTags, area, stack, onAdd, onDelete } = props;

  const {
    results: areas,
    isInitialLoading,
    fetchNextPage,
    ...infinityProps
  } = useInfiniteService({
    ...areasQueryKeys.list(mountFilters(area))._ctx.infinity,
    staleTime,
  });

  return (
    <div className="w-full pt-2 flex flex-col gap-2.5 overflow-hidden">
      <ConditionalRenderer condition={stack.value.length}>
        <TopBar stack={stack} />
      </ConditionalRenderer>
      <ConditionalRenderer condition={isInitialLoading}>
        <div className="flex justify-center w-full text-primary">
          <LoadingIcon className="w-5 h-5" />
        </div>
      </ConditionalRenderer>
      <InfinityList
        className="flex flex-col h-36 pr-2"
        scroll
        {...infinityProps}
        onReachEnd={fetchNextPage}
      >
        {areas.map(area => (
          <TagFactory
            key={area.id}
            currentTags={currentTags}
            area={area}
            stack={stack}
            onAdd={onAdd}
            onDelete={onDelete}
          />
        ))}
      </InfinityList>
    </div>
  );
}

type TagFactoryProps = {
  currentTags: TagWithName[];
  area: Area;
  stack: ReturnType<typeof useStack<Area>>;
  onAdd: (tag: TagWithName) => void;
  onDelete: (tagId: number) => void;
};

function TagFactory(props: TagFactoryProps) {
  const { currentTags, area, stack, onAdd, onDelete } = props;

  return (
    <ConditionalRenderer
      condition={area.isLeaf}
      fallback={<AreaTag area={area} stack={stack} />}
    >
      <LeafTags
        currentTags={currentTags}
        area={area}
        stack={stack}
        onAdd={onAdd}
        onDelete={onDelete}
      />
    </ConditionalRenderer>
  );
}

type LeafTagsProps = {
  currentTags: TagWithName[];
  area: Area;
  stack: ReturnType<typeof useStack<Area>>;
  onAdd: (tag: TagWithName) => void;
  onDelete: (tagId: number) => void;
};

function LeafTags(props: LeafTagsProps) {
  const { currentTags, area, stack, onAdd, onDelete } = props;

  const { results: tags } = useListService({
    ...tagsQueryKeys.list({
      areaId: [area.id],
    }),
    staleTime,
    enabled: !!area.id,
  });

  const tagsWithName = tags.map(tag => ({
    ...tag,
    name: mountTopicTagName([...stack.value, area]),
  }));

  return (
    <ul key={area.id} className="flex flex-col gap-1">
      {tagsWithName.map(tag => (
        <LeafTag
          key={tag.id}
          currentTags={currentTags}
          tag={tag}
          area={area}
          stack={stack}
          onAdd={onAdd}
          onDelete={onDelete}
        />
      ))}
    </ul>
  );
}

type LeafTagProps = {
  currentTags: TagWithName[];
  tag: TagWithName;
  area: Area;
  stack: ReturnType<typeof useStack<Area>>;
  onAdd: (tag: TagWithName) => void;
  onDelete: (tagId: number) => void;
};

function LeafTag(props: LeafTagProps) {
  const { currentTags, tag, area, stack, onAdd, onDelete } = props;

  const contain = currentTags.some(t => t.id === tag.id);

  const ref = useRef<HTMLInputElement>(null);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    if (checked) {
      onAdd({
        ...tag,
        name: mountTopicTagName([...stack.value, area]),
      });
      return;
    }

    onDelete(tag.id);
  };

  return (
    <li
      onClick={() => ref.current?.click()}
      key={tag.id}
      className={twJoin(
        'flex justify-between cursor-pointer rounded-md px-1',
        'hover:bg-primary-content hover:text-primary transition-all duration-150 ease-in-out',
      )}
    >
      <label htmlFor={'area' + tag.id} className="cursor-pointer">
        {area.name}
      </label>
      <Checkbox
        id={'area' + tag.id}
        value={tag.id}
        checked={contain}
        onChange={onChange}
        onClick={e => e.stopPropagation()}
        ref={ref}
      />
    </li>
  );
}

type AreaTagProps = {
  area: Area;
  stack: ReturnType<typeof useStack<Area>>;
};

function AreaTag(props: AreaTagProps) {
  const { area, stack } = props;

  return (
    <li
      key={area.id}
      onClick={() => stack.add(area)}
      className="flex justify-between cursor-pointer"
    >
      <p className={bodyText}>{area.name}</p>
      <ArrowRightIcon className="h-4 w-4 text-primary" />
    </li>
  );
}

function mountFilters(area?: Area): ListAreaFilter {
  if (area)
    return {
      parentAreaId: [area.id],
    };

  return {
    isRoot: true,
  };
}
